import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  getAdminItemByUrl,
  getAdminItemById,
  translateAdminItem,
  type AdminItem,
  type TranslateFn,
} from '@app/constants/admin-config';
import usePermissionVerify from '@app/hooks/permission-verify';

/**
 * Hook to get admin configuration data for the current route
 * @returns AdminItem configuration for the current page (title/description translated)
 * @throws Error if no config found for the current route
 */
export const useAdminConfig = (): AdminItem => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const config = getAdminItemByUrl(location.pathname);

  if (!config) {
    throw new Error(
      `No admin configuration found for route: ${location.pathname}`,
    );
  }

  return translateAdminItem(config, t);
};

/**
 * Hook to get admin configuration data by a specific URL or ID
 * @param identifier - URL path or item ID to look up
 * @returns AdminItem configuration with title/description translated, or null if not found
 */
export const useAdminConfigById = (identifier: string): AdminItem | null => {
  const { t } = useTranslation('admin');
  // First try by URL, then by ID if URL lookup fails
  const config = getAdminItemByUrl(identifier) ?? getAdminItemById(identifier);
  return config ? translateAdminItem(config, t) : null;
};

/**
 * Helper function to get title and description from admin config by ID
 * @param id - Admin item ID to look up
 * @param t - i18next translate function
 * @returns Object with translated title and description, or null if not found
 */
export const getAdminConfigData = (
  id: string,
  t: TranslateFn,
): { title: string; description: string } | null => {
  const config = getAdminItemById(id);
  if (!config) return null;

  const translated = translateAdminItem(config, t);
  return {
    title: translated.title,
    description: translated.description,
  };
};

/**
 * Hook to get admin configuration with permission checking
 * @param identifier - URL path or item ID to look up
 * @returns Object with config and permission status, or null if not found/no access
 *
 * TODO: Refactor to use checkPermissionRequirement + enriched permissions for consistency with orGroup/anyOf/synthetic permission support
 */
export const useAdminConfigWithPermissions = (
  identifier?: string,
): {
  config: AdminItem | null;
  hasAccess: boolean;
} => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const configId = identifier || location.pathname;

  // Get config by URL first, then by ID
  const config =
    getAdminItemByUrl(configId) || getAdminItemById(configId) || null;

  // Check permissions
  const userPermissions = usePermissionVerify(config?.permissions || []);

  // Check if user has ALL required permissions (supports inverted permissions prefixed with '!')
  const hasAccess = config?.permissions?.length
    ? config.permissions.every((permission) => {
        if (permission.startsWith('!')) {
          const actual = permission.slice(1);
          return !userPermissions[actual];
        }
        return Boolean(userPermissions[permission]);
      })
    : true;

  return {
    config: config ? translateAdminItem(config, t) : null,
    hasAccess,
  };
};

export default useAdminConfig;
